import { useState, useEffect, useRef } from 'react';
import api from '../api/axios';

// Cache par church_id pour éviter de refetcher inutilement
// Clé : church_id (ou 'global' pour le singleton sans église)
const _cacheByChurch = {};
const _listenersByChurch = {};

function getListeners(key) {
  if (!_listenersByChurch[key]) _listenersByChurch[key] = [];
  return _listenersByChurch[key];
}

function notifyListeners(key, data) {
  _cacheByChurch[key] = data;
  getListeners(key).forEach(fn => fn(data));
}

export async function refreshChurchSettings(churchId) {
  const key = churchId || 'global';
  try {
    const params = churchId ? { church_id: churchId } : {};
    const res = await api.get('/notifications/church-settings/', { params });
    notifyListeners(key, res.data);
    return res.data;
  } catch {
    return null;
  }
}

export const DEFAULT_SETTINGS = {
  church_name: 'RCC',
  logo: null,
  colors: {
    nouveau:   { from: '#000000', to: '#000000' },
    membre:    { from: '#000000', to: '#000000' },
    serviteur: { from: '#000000', to: '#000000' },
    invite:    { from: '#000000', to: '#000000' },
  },
  watermark_text: '',
  watermark_opacity: 0.08,
  card_bottom_text: 'Scannez le QR pour marquer votre présence',
  card_photo_caption: '',
  landing_title: '',
  landing_subtitle: '',
  landing_verse: '',
  landing_verse_ref: '',
  landing_bg_from: '#0f172a',
  landing_bg_to: '#1e3a8a',
};

export default function useChurchSettings(churchId) {
  const key = churchId || 'global';
  const [settings, setSettings] = useState(_cacheByChurch[key] || DEFAULT_SETTINGS);
  const prevKeyRef = useRef(key);

  useEffect(() => {
    const currentKey = churchId || 'global';

    // Si la clé a changé (changement d'église), mettre à jour immédiatement
    if (prevKeyRef.current !== currentKey) {
      prevKeyRef.current = currentKey;
      setSettings(_cacheByChurch[currentKey] || DEFAULT_SETTINGS);
    }

    // S'abonner aux mises à jour pour cette église
    const listeners = getListeners(currentKey);
    listeners.push(setSettings);

    // Fetch si pas encore en cache
    if (!_cacheByChurch[currentKey]) {
      refreshChurchSettings(churchId);
    }

    return () => {
      const ls = getListeners(currentKey);
      const idx = ls.indexOf(setSettings);
      if (idx !== -1) ls.splice(idx, 1);
    };
  }, [key, churchId]);

  return settings;
}
